import { hashKey, QueryKey } from "@tanstack/react-query";

const PAGE_PARAM_KEY = "page";

// Rebuilds the query key with any `page` field stripped from its param objects
// so two keys that differ only by page hash to the same value.
function stripPageParam(queryKey: QueryKey): unknown[] {
    return queryKey.map((part) => {
        if (
            part !== null &&
            typeof part === "object" &&
            !Array.isArray(part) &&
            PAGE_PARAM_KEY in part
        ) {
            return Object.fromEntries(
                Object.entries(part).filter(([key]) => key !== PAGE_PARAM_KEY),
            );
        }
        return part;
    });
}

/**
 * `placeholderData` factory that keeps the previous page on screen while the
 * next one loads, but only when the transition is a pure page change. Any other
 * change to the query key (workspace, filters, sorting, selected resource, …)
 * drops the placeholder so stale rows are never presented — or acted upon — for
 * a view they no longer belong to.
 */
export function keepPreviousDataOnPageChange(currentQueryKey: QueryKey) {
    const currentPageAgnosticHash = hashKey(stripPageParam(currentQueryKey));

    return <TData>(
        previousData: TData | undefined,
        previousQuery: { queryKey: QueryKey } | undefined,
    ): TData | undefined => {
        if (previousQuery === undefined) {
            return undefined;
        }

        const previousPageAgnosticHash = hashKey(
            stripPageParam(previousQuery.queryKey),
        );

        return previousPageAgnosticHash === currentPageAgnosticHash
            ? previousData
            : undefined;
    };
}
